/* eslint-disable no-continue */
import React from 'react';
import {
  isEmpty,
  isObject,
  isArray,
  startsWith,
  isString,
  forOwn,
  startCase
} from 'lodash-es';
import localStorageService from 'services/localStorage';
import store from 'redux/store';
import { osName } from 'react-device-detect';

import i18n from 'services/i18n';
import { Tracker } from 'services/tracking';
import {
  BENEFICIARY_TOKENIZATION,
  CARD_ISSUING_ACTIVATE,
  CARD_ISSUING_DETAILS,
  CARD_ISSUING_RESET_PIN,
  CARD_TOKENIZATION,
  CUSTOMER_TOKENIZATION_ROUTE,
  HOSTED_CARD_TOKENIZATION,
  HOSTED_PAGE_PREVIEW,
  IDV,
  OTP,
  PAYOUT_TRACKER_PAGE,
  SUBSCRIPTION,
  PAY,
  REUSABLE_LINK,
  CARD_ISSUING_SET_KBA,
  CARD_ISSUING_VIEW_PIN,
  DEMO
} from 'routes/paths';
import { hideModal, showModal } from 'redux/actions/global';
import { COLORS } from 'components/containers/Theme/defaults';
import MODAL_TYPES from 'components/containers/Modal/Modals/types';
import {
  CTA_BUTTON_LEGACY_TEXT,
  GA_ACTIONS,
  GA_CATEGORIES,
  GA_LABEL,
  HOSTED_PAGE_TYPES,
  HOSTED_PAGE_TYPES_TOKENS,
  MINIMUM_CONTRAST,
  YES_NO_OPTIONS
} from 'consts';

export const isIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

export const mask = pattern =>
  pattern.split('').reduce((maskedString, char) => {
    if (char === '*') {
      return maskedString + decodeEntities('&bull;');
    }

    return maskedString + char;
  }, '');

export const logoError = e => {
  console.log(e);
  e.target.style.display = 'none';
};

export const getFieldBooleanValue = value => value === YES_NO_OPTIONS.YES;

export const removeUnderscores = (str = '') => str.replace(/_/g, ' ');

export const getHostedPageType = () => {
  let hostedPageType;
  const { pathname } = window.location;

  if (pathname.includes(BENEFICIARY_TOKENIZATION)) {
    hostedPageType = HOSTED_PAGE_TYPES.beneficiaryTokenization;
  } else if (pathname.includes(SUBSCRIPTION)) {
    hostedPageType = HOSTED_PAGE_TYPES.subscription;
  } else if (pathname.includes(CARD_TOKENIZATION)) {
    hostedPageType = HOSTED_PAGE_TYPES.cardTokenization;
  } else if (pathname.includes(CARD_ISSUING_DETAILS)) {
    hostedPageType = HOSTED_PAGE_TYPES.cardIssuing;
  } else if (pathname.includes(CARD_ISSUING_ACTIVATE)) {
    hostedPageType = HOSTED_PAGE_TYPES.cardIssuingActivate;
  } else if (pathname.includes(CARD_ISSUING_RESET_PIN)) {
    hostedPageType = HOSTED_PAGE_TYPES.cardIssuingResetPIN;
  } else if (pathname.includes(CARD_ISSUING_VIEW_PIN)) {
    hostedPageType = HOSTED_PAGE_TYPES.cardIssuingResetPIN;
  } else if (pathname.includes(CARD_ISSUING_SET_KBA)) {
    hostedPageType = HOSTED_PAGE_TYPES.cardIssuingSetKba;
  } else if (pathname.includes(IDV)) {
    hostedPageType = HOSTED_PAGE_TYPES.idv;
  } else if (pathname.includes(OTP)) {
    hostedPageType = HOSTED_PAGE_TYPES.otp;
  } else if (pathname.includes(CUSTOMER_TOKENIZATION_ROUTE)) {
    hostedPageType = HOSTED_PAGE_TYPES.customerTokenization;
  } else if (pathname.includes(PAYOUT_TRACKER_PAGE)) {
    hostedPageType = HOSTED_PAGE_TYPES.payoutTracker;
  } else if (pathname.includes(REUSABLE_LINK)) {
    hostedPageType = HOSTED_PAGE_TYPES.reusable_link;
  } else if (pathname.includes(PAY)) {
    hostedPageType = HOSTED_PAGE_TYPES.reusable_link;
  } else if (pathname.includes(DEMO)) {
    hostedPageType = HOSTED_PAGE_TYPES.demo;
  } else {
    hostedPageType = HOSTED_PAGE_TYPES.checkout;
  }

  return hostedPageType;
};

export const getHostedPageFlowType = () => {
  const { global } = store.getState();
  const { isDemo: isDemoChild } = global;

  let hostedPageType = getHostedPageType();
  const { search } = window.location;

  if (search.includes(HOSTED_PAGE_TYPES_TOKENS[HOSTED_PAGE_TYPES.cardIssuingActivate])) {
    hostedPageType = HOSTED_PAGE_TYPES.cardIssuingActivate;
  } else if (
    search.includes(HOSTED_PAGE_TYPES_TOKENS[HOSTED_PAGE_TYPES.cardIssuingResetPIN])
  ) {
    hostedPageType = HOSTED_PAGE_TYPES.cardIssuingResetPIN;
  } else if (
    search.includes(HOSTED_PAGE_TYPES_TOKENS[HOSTED_PAGE_TYPES.cardIssuingSetKba])
  ) {
    hostedPageType = HOSTED_PAGE_TYPES.cardIssuingSetKba;
  }

  return isDemoChild ? HOSTED_PAGE_TYPES.demo : hostedPageType;
};

export const isUrl = str => {
  const expression =
    /[-a-zA-Z0-9@:%_+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?/gi;
  const regex = new RegExp(expression);
  return str.match(regex);
};

export const isPdfUrl = str => {
  const expression =
    /[-a-zA-Z0-9@:%_+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?\.pdf/gi;
  const regex = new RegExp(expression);
  return str.match(regex);
};

export const isBase64 = str => {
  try {
    return btoa(atob(str)) === str;
  } catch (err) {
    return false;
  }
};

export const isImgBase64 = str => {
  return startsWith(str, 'data:image/');
};

export const encodeBase64 = str => {
  return window.btoa(unescape(encodeURIComponent(str)));
};

export const addDays = (days, date = new Date()) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const formatDate = (date, locale = 'en-US') => {
  const createdDate = new Date(date);

  const dateTimeFormat = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: '2-digit'
  });
  const format = dateTimeFormat.formatToParts(createdDate);
  return {
    day: format.find(el => el.type === 'day')?.value,
    month: format.find(el => el.type === 'month')?.value,
    year: format.find(el => el.type === 'year')?.value
  };
};

export const getLocalTimeString = date => {
  const createdDate = new Date(date);

  let hours = createdDate.getHours().toString();
  if (hours.length === 1) {
    hours = `0${hours}`;
  }

  let minutes = createdDate.getMinutes().toString();
  if (minutes.length === 1) {
    minutes = `0${minutes}`;
  }

  return `${hours}:${minutes}`;
};

export const getDateFormatted = (unixSeconds, locale) => {
  const unixTimestampMS = unixSeconds * 1000;
  const time = getLocalTimeString(unixTimestampMS);
  const { month, day, year } = formatDate(unixTimestampMS, locale);

  return {
    time,
    month,
    day,
    year
  };
};

export const getDateFormatedString = ({
  showTime = false,
  locale = null,
  unixSeconds
}) => {
  const overrideLocal = locale || i18n.language;
  const { time, month, day, year } = getDateFormatted(unixSeconds, overrideLocal);

  return `${day} ${month} ${year} ${showTime ? time : ''}`;
};

export const getUnixSecondsFromDateObj = dateObj =>
  dateObj instanceof Date
    ? dateObj.getTime() / 1000
    : Math.round(new Date().getTime() / 1000);

export const getDeviceTimeZone = () => {
  try {
    return new Date().toString().split(' ')[5];
  } catch (e) {
    return null;
  }
};

export const copyToClipboard = str => {
  const el = document.createElement('textarea');
  el.value = str;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
};

export const decodeEntities = (function () {
  // this prevents any overhead from creating the object each time
  const element = document.createElement('div');

  function decodeHTMLEntities(str) {
    let res = str;
    if (res && typeof res === 'string') {
      // strip script/html tags
      res = str.replace(/<script[^>]*>([\S\s]*?)<\/script>/gim, '');
      res = str.replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gim, '');
      element.innerHTML = str;
      res = element.textContent;
      element.textContent = '';
    }

    return res;
  }

  return decodeHTMLEntities;
})();

export const hex2Rgb = hex => {
  const { style } = new Option();
  if (style.color !== '') return false;
  style.color = hex;
  const rgbaString = style.color;
  return rgbaString.match(/[\d.]+/g);
};

const luminanace = (r, g, b) => {
  const a = [r, g, b].map(v => {
    const value = v / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });

  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

export const detectColorType = color => {
  if (color.indexOf('#') === 0) return 'hex';

  if (color.match(/rgba?/)) return 'rgb';

  return 'namedColor';
};

export const validColor = val => {
  const { style } = new Option();
  if (style.color !== '') return false;
  style.color = val;
  return style.color !== '';
};

export const contrast = (color1, color2) => {
  const colorsArray = [color1, color2].filter(validColor);

  if (colorsArray.length !== 2) return null;

  const red = [];
  const green = [];
  const blue = [];

  colorsArray.forEach(color => {
    let r, g, b;
    const type = detectColorType(color);
    if (type === 'namedColor') {
      const randomDiv = document.createElement('div');
      randomDiv.style.color = color;
      document.body.appendChild(randomDiv);
      [r, g, b] = window.getComputedStyle(randomDiv).color.match(/\d+/g);
      randomDiv.remove();
    } else if (type === 'hex') {
      [r, g, b] = hex2Rgb(color);
    } else if (type === 'rgb') {
      [r, g, b] = color.match(/\d+/g);
    }

    red.push(r);
    green.push(g);
    blue.push(b);
  });

  const lum1 = luminanace(red[0], green[0], blue[0]) + 0.05;
  const lum2 = luminanace(red[1], green[1], blue[1]) + 0.05;
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return brightest / darkest;
};

export const fixContrastAgainstWhiteBg = (colorToCheck, defaultColor) => {
  const bgContrast = contrast(colorToCheck, COLORS.WHITES[1]);

  if (bgContrast < MINIMUM_CONTRAST) {
    return defaultColor;
  }

  return colorToCheck;
};

export const isFunction = func => typeof func === 'function';

export const getCountdown = (now, future) => {
  // get total seconds between the times
  let delta = Math.abs(future - now);

  // calculate (and subtract) whole days
  const days = Math.floor(delta / 86400);
  delta -= days * 86400;

  // calculate (and subtract) whole hours
  const hours = Math.floor(delta / 3600) % 24;
  delta -= hours * 3600;

  // calculate (and subtract) whole minutes
  const minutes = Math.floor(delta / 60) % 60;
  delta -= minutes * 60;

  // what's left is seconds
  const seconds = delta % 60; // in theory the modulus is not required

  if (days > 0) {
    return {
      number: days,
      unit: days === 1 ? 'day' : 'days'
    };
  }

  if (hours > 0) {
    return {
      number: hours,
      unit: hours === 1 ? 'hour' : 'hours'
    };
  }

  if (minutes > 0) {
    return {
      number: minutes,
      unit: minutes === 1 ? 'minute' : 'minutes'
    };
  }

  if (seconds > 0) {
    return {
      number: seconds,
      unit: seconds === 1 ? 'second' : 'seconds'
    };
  }

  return null;
};

const capitalize = name => name.charAt(0).toUpperCase() + name.slice(1);

const capAndJoin = words => words.map(capitalize).join(' ');

const removeUnderScores = title => capAndJoin(title.split(/_/g));

const removeSpaces = title => capAndJoin(title.split(/\s/g));

const removeDashes = title => capAndJoin(title.split(/-/g));

export const capitalizeAndRemoveSpecialChars = title =>
  capitalize(
    removeSpaces(removeDashes(removeUnderScores(title))).replace(
      /([a-z])([A-Z])/g,
      '$1 $2'
    )
  );

export const convertToStartCase = textToStartCase => startCase(textToStartCase);

export const fileToDataUri = file =>
  new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = event => {
      resolve(event.target.result);
    };
    reader.readAsDataURL(file);
  });

export const parseNumber = n => (Boolean(n) === true ? Number(n) : 0);

export const buildStringWithCommasByOrder = (obj, fieldsOrder) => {
  if (isEmpty(obj)) return '';

  return fieldsOrder.reduce((acc, currentField) => {
    const currentValue = obj[currentField];
    return currentValue ? (acc ? `${acc}, ${currentValue}` : currentValue) : acc;
  }, '');
};

export const snakeToCamel = str =>
  str.replace(/([-_][a-z])/g, group =>
    group.toUpperCase().replace('-', '').replace('_', '')
  );

export const camelToSnakeCase = str =>
  str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

export const isObjectNotEmpty = obj => isObject(obj) && !isEmpty(obj);

export const isObjectNotArray = obj => isObject(obj) && !isArray(obj);

export const getReferrer = () => {
  const hostedPageType = getHostedPageType();
  const pageToken = localStorageService.getItem(HOSTED_PAGE_TYPES_TOKENS[hostedPageType]);
  const referrer = localStorageService.getItem(`${pageToken}_referrer`) || '';

  return referrer || '';
};

export const getRedirectUrl = ({ url = '', merchantWebsite = '' }) => {
  if (!url && !merchantWebsite) {
    return getReferrer();
  }

  return url || merchantWebsite;
};

export const parseToRequest = requiredFields => {
  const fields = {};

  Object.values(requiredFields).forEach(field => {
    let value;
    if (field.type === 'boolean') {
      value = getFieldBooleanValue(field.value);
    } else {
      value = field.value;
    }
    fields[field.name] = value;
  });

  return fields;
};

export const getClientDetails = (sessionId, isMobile) => {
  const fingerprint = localStorageService.getItem('fp');

  const details = {
    session_id: sessionId,
    device_timezone: getDeviceTimeZone(),
    operating_system: osName,
    user_agent: navigator.userAgent,
    device_fingerprint: fingerprint,
    language: i18n.language,
    java_enabled: navigator.javaEnabled(),
    javascript_enabled: true,
    screen_color_depth: window.screen.colorDepth,
    screen_height: window.screen.height,
    screen_width: window.screen.width,
    window_height: window.innerHeight,
    window_width: window.innerWidth,
    timezone_offset: new Date().getTimezoneOffset()
  };

  if (isMobile) {
    details.device_mobile = true;
  }

  return details;
};

export const getUrlParams = url => {
  if (!url) return {};

  const hashes = url.slice(url.indexOf('?') + 1).split('&');
  const params = {};
  hashes.forEach(hash => {
    const [key, val] = hash.split('=');
    params[key] = decodeURIComponent(val);
  });
  return params;
};

export const traverse = (obj, cb) => {
  Object.entries(obj).forEach(([key, val]) => {
    cb(key, val, obj);
    if (val && typeof val === 'object') {
      traverse(val, cb);
    }
  });
};

export const isEmptyObj = value => isObject(value) && isEmpty(value);

export const removeEmpty = obj => {
  if (!obj) return obj;
  const filterOutFalsyValuesFromObjParam = key => Boolean(obj[key]);
  const recusivelyReducerFunc = (acc, positiveKey) => {
    if (isEmptyObj(obj[positiveKey])) return acc;

    acc[positiveKey] = isObject(obj[positiveKey])
      ? removeEmpty(obj[positiveKey])
      : obj[positiveKey];
    return acc;
  };
  return Object.keys(obj)
    .filter(filterOutFalsyValuesFromObjParam)
    .reduce(recusivelyReducerFunc, {});
};

export const objectsMerger = ({ defaultObj, sourceObj }) => {
  if (!defaultObj || !sourceObj) return defaultObj;
  let mergedObj = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const [defaultKey, defaultValue] of Object.entries(defaultObj)) {
    const newValue = sourceObj[defaultKey] ? sourceObj[defaultKey] : defaultValue;

    let newEntryObject;

    if (isEmptyObj(newValue)) {
      newEntryObject = { [defaultKey]: defaultValue };
      mergedObj = { ...mergedObj, ...newEntryObject };

      continue;
    }

    if (isObject(newValue)) {
      newEntryObject = {
        [defaultKey]: objectsMerger({ sourceObj: newValue, defaultObj: defaultValue })
      };
      mergedObj = { ...mergedObj, ...newEntryObject };

      continue;
    }

    newEntryObject = { [defaultKey]: newValue };
    mergedObj = { ...mergedObj, ...newEntryObject };
  }
  return mergedObj;
};

export const getCallLink = (
  phone,
  text,
  eventCategory = GA_CATEGORIES.GENERAL_ERROR_MESSAGE
) => {
  const tracker = new Tracker(getHostedPageFlowType());
  const { isMobile } = store.getState().global;

  if (isMobile) {
    return (
      <a
        key="call_link"
        href={`tel:${phone}`}
        onClick={() =>
          tracker.fireEvent({
            category: `${GA_ACTIONS.CLICK} - ${eventCategory} - ${GA_ACTIONS.CUSTOMER_SUPPORT} - ${GA_LABEL.PHONE}`,
            action: eventCategory
          })
        }
      >
        {capitalize(i18n.t(text))} {phone}
      </a>
    );
  }

  return `${i18n.t(text)} ${phone}`;
};

export const getEmailLink = (
  email,
  text,
  eventCategory = GA_CATEGORIES.GENERAL_ERROR_MESSAGE
) => {
  const tracker = new Tracker(getHostedPageFlowType());
  return (
    <a
      key="email_link"
      href={`mailto:${email}`}
      onClick={() =>
        tracker.fireEvent({
          category: `${GA_ACTIONS.CLICK} - ${eventCategory} - ${GA_ACTIONS.CUSTOMER_SUPPORT} - ${GA_LABEL.EMAIL}`,
          action: eventCategory
        })
      }
    >
      {i18n.t(text)}
    </a>
  );
};

export const getHelpCenterLink = (
  url,
  eventCategory = GA_CATEGORIES.GENERAL_ERROR_MESSAGE
) => {
  const tracker = new Tracker(getHostedPageFlowType());
  return (
    <a
      key="help_center_link"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        tracker.fireEvent({
          category: `${GA_ACTIONS.CLICK} - ${eventCategory} - ${GA_ACTIONS.CUSTOMER_SUPPORT} - ${GA_LABEL.SUPPORT_URL}`,
          action: eventCategory
        })
      }
    >
      {i18n.t('customerSupport.help_center')}
    </a>
  );
};

export const getCustomerSupportError = ({
  prefixKey = 'customerSupport.issue_persists',
  getPrefixFunction = null
} = {}) => {
  const {
    customerSupport: { email, phoneNumber, url }
  } = store.getState().global.merchant;
  const hasCustomerSupportDetails = Boolean(email || phoneNumber || url);

  if (!hasCustomerSupportDetails) return '';

  let prefix;
  if (isFunction(getPrefixFunction)) {
    prefix = getPrefixFunction({ hasCustomerSupportDetails });
  } else {
    prefix = i18n.t(prefixKey);
  }
  const suffix = getCustomerSupportErrorPart();

  return (
    <>
      {prefix} <br /> {suffix}
    </>
  );
};

export const getCustomerSupportErrorPart = () => {
  const {
    customerSupport: { email, phoneNumber, url }
  } = store.getState().global.merchant;

  const fieldsMapping = {
    0: {
      value: phoneNumber,
      renderElement: () => getCallLink(phoneNumber, 'createPaymentErrors.call_text')
    },
    1: {
      value: email,
      renderElement: () => getEmailLink(email, 'customerSupport.email')
    },
    2: {
      value: url,
      renderElement: () => getHelpCenterLink(url)
    }
  };
  const elements = [];

  Object.entries(fieldsMapping).forEach(([index, field]) => {
    const { value, renderElement } = field;
    if (!value) return;

    const fieldsBefore = Object.values(fieldsMapping).slice(0, index);
    const fieldsBeforeWithValue = fieldsBefore.filter(field => Boolean(field.value));

    if (fieldsBeforeWithValue.length) {
      elements.push(' | ');
    }
    elements.push(renderElement());
  });

  return elements;
};

export const isHostedCardToolkit = () =>
  window?.location?.pathname === HOSTED_CARD_TOKENIZATION;
export const isHostedPagePreview = () =>
  window?.location?.pathname === HOSTED_PAGE_PREVIEW;

export const getButtonColor = organizationColor => {
  if (validColor(organizationColor)) return organizationColor;
  const newColor = '#'.concat(organizationColor);
  if (validColor(newColor)) return newColor;

  return null;
};

export const getColor = organizationColor => {
  const { sdkParams, previewConfig } = store.getState().global;
  const { btnColor: sdkBtnColor } = sdkParams;
  const { btnColor: previewBtnColor } = previewConfig;

  const btnColor = previewBtnColor || sdkBtnColor;

  if (btnColor) return btnColor;
  return getButtonColor(organizationColor);
};

export const getText = defaultText => {
  const { sdkParams, previewConfig } = store.getState().global;
  const { btnText: sdkBtnText } = sdkParams;
  const merchantText = CTA_BUTTON_LEGACY_TEXT[defaultText] || `cta_texts.${defaultText}`;
  let { ctaText: previewCtaText } = previewConfig;
  previewCtaText = previewCtaText ? `cta_texts.${previewCtaText}` : '';
  return previewCtaText || sdkBtnText || merchantText;
};

export const getErrorMessageCustomerSupport = ({ t, email, phoneNumber, url }) => {
  if (!email && !phoneNumber && !url) {
    return null;
  }
  let message;

  const callLink = getCallLink(
    phoneNumber,
    'errorPage500.call_text',
    GA_CATEGORIES.ERROR_500
  );
  const emailLink = getEmailLink(
    email,
    'errorPage500.email_text',
    GA_CATEGORIES.ERROR_500
  );
  const helpCenterLink = getHelpCenterLink(url, GA_CATEGORIES.ERROR_500);
  const urgentSituations = (
    <>
      {`${t('errorPage500.urgent_situations')}`} {helpCenterLink}{' '}
      {`${t('errorPage500.additional_information')}`}
    </>
  );

  if (phoneNumber && email && url) {
    message = (
      <>
        {`${t('errorPage500.immediate_help')}, ${t('please')} `}
        {callLink}
        {` ${t('or')} `}
        {emailLink} {urgentSituations}
      </>
    );
  } else if (phoneNumber && !email && !url) {
    message = (
      <>
        {`${t('errorPage500.immediate_help')}, ${t('please')}`} {callLink}.
      </>
    );
  } else if (email && !phoneNumber && !url) {
    message = (
      <>
        {`${t('errorPage500.immediate_help')}, ${t('please')} `} {emailLink}.
      </>
    );
  } else if (url && !phoneNumber && !email) {
    message = (
      <>
        {`${t('errorPage500.try_again_later')} `} {helpCenterLink}{' '}
        {`${t('errorPage500.additional_information')}`}
      </>
    );
  } else if (phoneNumber && email && !url) {
    message = (
      <>
        {`${t('errorPage500.immediate_help')}, ${t('please')} `} {callLink}{' '}
        {`${t('or')} `} {emailLink}.
      </>
    );
  } else if (phoneNumber && url && !email) {
    message = (
      <>
        {`${t('errorPage500.immediate_help')}, ${t('please')} `} {callLink}. {'\n'}{' '}
        {urgentSituations}
      </>
    );
  } else if (email && url && !phoneNumber) {
    message = (
      <>
        {`${t('errorPage500.immediate_help')}, ${t('please')} `} {emailLink}. {'\n'}{' '}
        {urgentSituations}
      </>
    );
  }

  return message;
};

export const preventUserFromGoingBackInHistory = () => {
  window.history.pushState(null, document.title, window.location.href);
  window.addEventListener('popstate', function (_) {
    window.history.pushState(null, document.title, window.location.href);
  });
};

export const isBoolean = value => typeof value === 'boolean';

export const backToWebsiteModal = ({ gaData = {}, tracker, redirectUrl, dispatch }) => {
  const { isPreview } = store.getState().global;

  if (!isEmpty(gaData)) {
    tracker.fireEvent({
      category: gaData.category,
      action: gaData.action,
      label: gaData.label
    });
  }

  const approveCallback = () => {
    if (isPreview) return;

    tracker.fireEvent({
      category: GA_CATEGORIES.ARE_YOU_SURE_POPUP,
      action: `${GA_ACTIONS.CLICK} - ${GA_CATEGORIES.ARE_YOU_SURE_POPUP} - Leave`
    });

    window.location.href = redirectUrl;
  };

  const cancelCallback = () => {
    tracker.fireEvent({
      category: GA_CATEGORIES.ARE_YOU_SURE_POPUP,
      action: `${GA_ACTIONS.CLICK} - ${GA_CATEGORIES.ARE_YOU_SURE_POPUP} - Stay`
    });

    dispatch(hideModal());
  };

  dispatch(
    showModal({
      type: MODAL_TYPES.CTA,
      props: {
        generalProps: {
          name: 'back-modal',
          title: i18n.t('modal.are_you_sure'),
          subTitle: i18n.t('modal.lost_changes'),
          secondaryButtonProps: {
            children: i18n.t('modal.leave'),
            onClick: approveCallback
          },
          primaryButtonProps: {
            children: i18n.t('modal.stay'),
            onClick: cancelCallback
          }
        }
      }
    })
  );
};

export const addQueryParamsToUrl = (originalUrl, paramsString) => {
  if (!isUrl(originalUrl)) return originalUrl;

  const updatedUrl = new URL(originalUrl);
  const isOriginalQueryParamEmpty = updatedUrl.search === '';

  if (isOriginalQueryParamEmpty) {
    updatedUrl.search = paramsString;
  } else {
    const aggregatedQueryParams = `${updatedUrl.search}&${paramsString}`;
    updatedUrl.search = aggregatedQueryParams;
  }

  return updatedUrl.href;
};

export const mapObjectValues = (object, cb) =>
  Object.fromEntries(Object.entries(object).map(([k, v]) => [k, cb(v)]));

export const executeFuncDelayed = ({ func, delay }) => {
  // passing explicit null in case we do not want to set timeout
  if (delay === null) {
    return func();
  }

  return setTimeout(() => {
    func();
  }, delay);
};

export const parseImageSrc = src => {
  if (!src) {
    return '';
  }
  if (isBase64(src)) {
    return `data:image/png;base64,${src}`;
  }
  return src;
};

export const tryTestRegex = (regex, value) => {
  try {
    return regex.test(value);
  } catch (err) {
    return false;
  }
};

export function replaceNonDigits(value) {
  return String(value).replace(/\D/g, '');
}

export const sanitizeNestedObjectValues = (object = {}, valuesToSanitize = []) => {
  if (!isObjectNotArray(object)) return;

  forOwn(object, (objectValue, objectKey) => {
    if (isString(objectValue)) {
      const isObjectValueDirty = valuesToSanitize.some(v =>
        objectValue?.toLowerCase?.().includes?.(v)
      );

      if (isObjectValueDirty) object[objectKey] = '';
    } else if (isObjectNotArray(objectValue)) {
      sanitizeNestedObjectValues(objectValue, valuesToSanitize);
    }
  });
};

export const parsePixelsToREM = value => value / 10;

export const chunkWordByLength = (value, length) => {
  return String(value)
    .match(new RegExp(`.{1,${length}}`, 'g'))
    ?.join('-');
};

export const chunkStringByLength = (value, maxLength, wordLength) => {
  return String(value)
    .split(' ')
    .reduce((prevStr, currStr) => {
      const parsedWord =
        currStr.length >= maxLength ? chunkWordByLength(currStr, wordLength) : currStr;
      return prevStr?.concat(parsedWord?.concat(' '));
    }, '');
};

export const parseRegex = (regex, flag) => {
  try {
    return new RegExp(regex, flag);
  } catch (e) {
    return null;
  }
};

export const getMerchantDataByPageToken = pageToken =>
  localStorageService.getParsedItem(`${pageToken}_merchant_data`);

export const parseQueryParams = () => {
  const query = new URLSearchParams(window.location.search);
  return Object.fromEntries(query.entries());
};
